import type { Router } from 'vue-router'

type PushActionType = 'callback' | 'url' | 'navigate'
declare let self: ServiceWorkerGlobalScope & { __WB_DISABLE_DEV_LOGS: boolean }

export interface ParsedPushAction {
  type: PushActionType
  payload: string
}

interface PushActionMessage {
  type: 'push-action'
  value: string
}

const registeredCallbacks = new Map<string, () => unknown | Promise<unknown>>()

let appRouter: Router | undefined

export const isServiceWorker = typeof window === 'undefined' && typeof self !== 'undefined' && typeof self.registration !== 'undefined'

export function registerPushCallback(name: string, callback: () => void): void {
  if (registeredCallbacks.has(name)) {
    console.warn(`[PushHandler] Callback for "${name}" already registered.`)
  }
  registeredCallbacks.set(name, callback)
}

export function parsePushAction(raw: string): ParsedPushAction | undefined {
  if (!raw.includes('://')) return undefined
  const [type, ...rest] = raw.split('://')
  const payload = rest.join('://')
  if (!['callback', 'url', 'navigate'].includes(type)) return undefined
  return { type: type as PushActionType, payload }
}
export function handlePushAction(raw: string): void {
  const parsed = parsePushAction(raw)
  if (!parsed) return

  if (isServiceWorker) {
    self.clients.matchAll({ type: 'window', includeUncontrolled: true }).then((clients) => {
      const target = clients.find((c) => 'focus' in c || 'postMessage' in c)
      if (target) {
        target.postMessage({ type: 'push-action', value: raw } satisfies PushActionMessage)
        try {
          target.focus?.()
        } catch (e) {
          console.warn('[PushHandler] Unable to focus client:', e)
        }
      } else {
        console.warn('[PushHandler] No available client to forward push action.')
      }
    })
    return
  }

  // In App direkt ausführen
  void (async () => {
    switch (parsed.type) {
      case 'callback': {
        const cb = registeredCallbacks.get(parsed.payload)
        if (cb) {
          try {
            await cb()
          } catch (e) {
            console.error('[PushHandler] Error in callback:', e)
          }
        } else {
          console.warn('[PushHandler] No callback registered for:', parsed.payload)
        }
        break
      }

      case 'url': {
        window.open(parsed.payload, '_blank')
        break
      }

      case 'navigate': {
        const path = parsed.payload.startsWith('/') ? parsed.payload : '/' + parsed.payload
        if (appRouter) {
          await appRouter.push(path)
        } else {
          window.location.href = path
        }
        break
      }
    }
  })()
}

let hasInitialized = false

export function setupPushHandler(router: Router): void {
  if (hasInitialized || typeof window === 'undefined' || !('serviceWorker' in navigator)) return
  hasInitialized = true
  appRouter = router

  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data as PushActionMessage
    if (data?.type === 'push-action') {
      handlePushAction(data.value)
    }
  })

  // Beispiel-Callbacks
  registerPushCallback('testCallback', () => {
    alert('[PushCallback] Test angekommen!')
  })
}
